import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProfileImage from "../../../../components/profileImage";

const MatchEvent = ({ event, handleScoreUpdate, handleAttendanceStatusChange }) => {
  const { t } = useTranslation();
  const [scores, setScores] = useState({
    team1: event?.event?.metaData?.score?.team1 || 0,
    team2: event?.event?.metaData?.score?.team2 || 0
  });

  const handleScoreChange = (teamId, newScore) => {
    setScores(prev => ({
      ...prev,
      [teamId === event.event.team1._id ? 'team1' : 'team2']: newScore
    }));
    handleScoreUpdate(teamId, newScore);
  };

  // Add null check for teams
  if (!event?.event?.team1 || !event?.event?.team2) {
    return null;
  }

  // Helper function to get team members
  const getTeamMembers = (teamId) => {
    // Ensure members is an array
    const members = Array.isArray(event.members) ? event.members : [];
    
    // Filter members by teamId directly
    return members.filter(member => member.teamId === teamId);
  };

  const team1Members = getTeamMembers(event.event.team1._id);
  const team2Members = getTeamMembers(event.event.team2._id);

  // Debug logging
  console.log('Team 1 ID:', event.event.team1._id);
  console.log('Team 2 ID:', event.event.team2._id);
  console.log('All members:', event.members);
  console.log('Team 1 members:', team1Members);
  console.log('Team 2 members:', team2Members);

  return (
    <>
      {/* Match Header */}
      <div className="flex items-center gap-4 mt-2">
        <div className="flex items-center gap-2">
          {event.event.team1?.logo && (
            <ProfileImage 
              image={event.event.team1.logo} 
              alt={event.event.team1.name}
              className="w-8 h-8 rounded-full"
            />
          )}
          <span className="text-white font-medium">{event.event.team1.name}</span>
        </div>
        <span className="text-white text-2xl font-bold">{t("matches.vs")}</span>
        <div className="flex items-center gap-2">
          {event.event.team2?.logo && (
            <ProfileImage 
              image={event.event.team2.logo} 
              alt={event.event.team2.name}
              className="w-8 h-8 rounded-full"
            />
          )}
          <span className="text-white font-medium">{event.event.team2.name}</span>
        </div>
      </div>

      {/* Match Score Section */}
      <div className="bg-gray-50 rounded-xl p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">{t("matches.matchScore")}</h3>
        <div className="grid grid-cols-2 gap-8">
          {/* Team 1 Score and Members */}
          <div className="space-y-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center gap-3 mb-4">
                {event.event.team1?.logo && (
                  <ProfileImage 
                    image={event.event.team1.logo} 
                    alt={event.event.team1.name}
                    className="w-12 h-12 rounded-full"
                  />
                )}
                <div>
                  <h4 className="font-medium text-gray-900">{event.event.team1.name}</h4>
                  <p className="text-sm text-gray-500">{event.event.team1.type}</p>
                </div>
              </div>
              {event.event.eventStatus === "In Progress" ? (
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => handleScoreChange(event.event.team1._id, scores.team1 - 1)}
                    className="px-3 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                    aria-label={t("matches.decreaseScore")}
                  >
                    -
                  </button>
                  <span className="text-3xl font-bold">{scores.team1}</span>
                  <button 
                    onClick={() => handleScoreChange(event.event.team1._id, scores.team1 + 1)}
                    className="px-3 py-1 bg-green-100 text-green-600 rounded hover:bg-green-200"
                    aria-label={t("matches.increaseScore")}
                  >
                    +
                  </button>
                </div>
              ) : (
                <span className="text-3xl font-bold">{scores.team1}</span>
              )}
            </div>

            {/* Team 1 Members */}
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="font-medium text-gray-900 mb-3">{t("teams.teamMembers")}</h4>
              <div className="space-y-3">
                {team1Members.length > 0 ? (
                  team1Members.map((member) => (
                    <div key={member.userId} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <ProfileImage
                          image={member.user.profilePicture}
                          className="w-8 h-8 rounded-full"
                        />
                        <div>
                          <p className="font-medium text-gray-900">
                            {member.user.firstName} {member.user.lastName}
                          </p>
                          <p className="text-sm text-gray-500 capitalize">
                            {member.accountType}
                          </p>
                        </div>
                      </div>
                      {(event.event.eventStatus === "In Progress" || event.event.eventStatus === "Completed") && handleAttendanceStatusChange ? (
                        <select
                          value={member.attendanceStatus}
                          onChange={(e) => handleAttendanceStatusChange(member.userId, e.target.value)}
                          className={`px-3 py-1 rounded-full text-sm border ${
                            member.attendanceStatus === "pending"
                              ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                              : member.attendanceStatus === "attended"
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-red-50 text-red-700 border-red-200"
                          }`}
                        >
                          <option value="pending">Pending</option>
                          <option value="attended">Attended</option>
                          <option value="absent">Absent</option>
                        </select>
                      ) : (
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          member.attendanceStatus === "pending"
                            ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
                            : member.attendanceStatus === "attended"
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : "bg-red-50 text-red-700 border border-red-200"
                        }`}>
                          {t(`events.${member.attendanceStatus}`)}
                        </span>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center">{t("teams.noMembersFound")}</p>
                )}
              </div>
            </div>
          </div>

          {/* Team 2 Score and Members */}
          <div className="space-y-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center gap-3 mb-4">
                {event.event.team2?.logo && (
                  <ProfileImage 
                    image={event.event.team2.logo} 
                    alt={event.event.team2.name}
                    className="w-12 h-12 rounded-full"
                  />
                )}
                <div>
                  <h4 className="font-medium text-gray-900">{event.event.team2.name}</h4>
                  <p className="text-sm text-gray-500">{event.event.team2.type}</p>
                </div>
              </div>
              {event.event.eventStatus === "In Progress" ? (
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => handleScoreChange(event.event.team2._id, scores.team2 - 1)}
                    className="px-3 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                    aria-label={t("matches.decreaseScore")}
                  >
                    -
                  </button>
                  <span className="text-3xl font-bold">{scores.team2}</span>
                  <button 
                    onClick={() => handleScoreChange(event.event.team2._id, scores.team2 + 1)}
                    className="px-3 py-1 bg-green-100 text-green-600 rounded hover:bg-green-200"
                    aria-label={t("matches.increaseScore")}
                  >
                    +
                  </button>
                </div>
              ) : (
                <span className="text-3xl font-bold">{scores.team2}</span>
              )}
            </div>

            {/* Team 2 Members */}
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="font-medium text-gray-900 mb-3">{t("teams.teamMembers")}</h4>
              <div className="space-y-3">
                {team2Members.length > 0 ? (
                  team2Members.map((member) => (
                    <div key={member.userId} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <ProfileImage
                          image={member.user.profilePicture}
                          className="w-8 h-8 rounded-full"
                        />
                        <div>
                          <p className="font-medium text-gray-900">
                            {member.user.firstName} {member.user.lastName}
                          </p>
                          <p className="text-sm text-gray-500 capitalize">
                            {member.accountType}
                          </p>
                        </div>
                      </div>
                      {(event.event.eventStatus === "In Progress" || event.event.eventStatus === "Completed") && handleAttendanceStatusChange ? (
                        <select
                          value={member.attendanceStatus}
                          onChange={(e) => handleAttendanceStatusChange(member.userId, e.target.value)}
                          className={`px-3 py-1 rounded-full text-sm border ${
                            member.attendanceStatus === "pending"
                              ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                              : member.attendanceStatus === "attended"
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-red-50 text-red-700 border-red-200"
                          }`}
                        >
                          <option value="pending">Pending</option>
                          <option value="attended">Attended</option>
                          <option value="absent">Absent</option>
                        </select>
                      ) : (
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          member.attendanceStatus === "pending"
                            ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
                            : member.attendanceStatus === "attended"
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : "bg-red-50 text-red-700 border border-red-200"
                        }`}>
                          {t(`events.${member.attendanceStatus}`)}
                        </span>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center">{t("teams.noMembersFound")}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MatchEvent; 